/**
 * Match an RDW plate record to a published `market_variants` row, in the browser.
 *
 * Port of `autouncle-scraper/src/kenteken/matcher.py`, which is the reference
 * implementation of `docs/KENTEKEN_MATCHING_SPEC.md` §1-§3d and carries the full
 * reasoning. Keep the two in step: the Python side is what the go/no-go hit rate
 * (89.8% of 500 real plates reach a published band) was measured with.
 *
 * The rules are not obvious and every one of them was measured, so the *why* stays inline:
 *
 * - The `variant`/`uitvoering` grain is unreachable — RDW ships type-approval codes
 *   (`C5P61`), we hold human trim names. `model_fuel` is the join target, `model` the fallback.
 * - `handelsbenaming` is free text, sometimes brand-prefixed (`CITROEN C1`) and sometimes
 *   bare (`ALTO`), so the make prefix must be stripped before comparing.
 * - A bare code and the same code plus a qualifier (`320`/`320e`, `206`/`206 CC`) is the
 *   ORDINARY shape of our vocabulary, not an error — so "exactly one candidate" is the wrong
 *   ambiguity guard. Longest candidate wins; only a tie at the longest length is ambiguous.
 * - A trailing `+` is powertrain-bearing (`RX450h+` is a plug-in and medians 2.16x `RX450h`),
 *   so it is promoted to a ` PLUS` token BEFORE punctuation is collapsed.
 * - `Abarth`/`Cupra`/`DS Automobiles` are NOT merged into `Fiat`/`Seat`/`Citroën`: their model
 *   vocabularies overlap (`500`, `Leon`, `DS4`), the sub-brand is 1.26x-4.85x dearer, and 89.8%
 *   of the plate mass on those keys is the cheap parent car. A coin flip there overvalues nine
 *   plates in ten — ambiguity is the honest answer.
 */

// --- §1 plate normalisation -------------------------------------------------

/**
 * `9-SZS-20` -> `9SZS20`, or null when it cannot be a Dutch plate.
 *
 * Deliberately loose: NO sidecode whitelist. The Dutch series is extended every few years,
 * so a hardcoded format list guarantees that some future-issued plate is rejected
 * client-side as "does not exist" — which re-creates the T6 prohibition (never report a
 * lookup failure as "plate not found") in a new place. RDW is the authority on existence;
 * our only job is to stop sending it a form it cannot parse.
 */
export function normalisePlate(raw) {
  if (!raw) return null
  const cleaned = String(raw).replace(/[^A-Za-z0-9]+/g, '').toUpperCase()
  return /^[A-Z0-9]{6}$/.test(cleaned) ? cleaned : null
}

// --- §2 fuel ----------------------------------------------------------------

const ELECTRIC = new Set(['ELEKTRICITEIT'])
const COMBUSTION = new Set(['BENZINE', 'DIESEL', 'LPG', 'ALCOHOL', 'CNG'])

/**
 * Collapse ALL of a plate's `8ys7-d773` rows into our vocabulary.
 *
 * `8ys7-d773` is keyed per fuel, not per vehicle: cycle 013 measured 300 vehicles -> 320
 * rows, and the excess is the hybrids. Reading the first row alone prices every plug-in
 * hybrid as whatever RDW happened to list first, so the derivation is set-based and the
 * ORDER of the tests matters: hybrid before electric before petrol.
 */
export function deriveFuel(descriptions) {
  const s = new Set(
    (descriptions || []).filter(Boolean).map(d => String(d).trim().toUpperCase())
  )
  if (s.size === 0) return 'other'
  const hasElectric = [...ELECTRIC].some(f => s.has(f))
  const hasCombustion = [...COMBUSTION].some(f => s.has(f))
  if (hasElectric && hasCombustion) return 'hybrid'
  if (hasElectric) return 'electric'
  if (s.has('BENZINE')) return 'petrol'
  if (s.has('DIESEL')) return 'diesel'
  // LPG and alcohol land here on purpose — `other` holds 427 published rows, and an LPG
  // car is not priced like a petrol one.
  return 'other'
}

// --- §3a make ---------------------------------------------------------------

const stripDiacritics = s => s.normalize('NFD').replace(/\p{Mn}/gu, '')

/** Uppercase, de-accent, collapse `-`/`&`/runs of space, drop a trailing parenthetical. */
export function normaliseMake(s) {
  if (!s) return ''
  let out = stripDiacritics(String(s)).toUpperCase()
  out = out.replace(/\s*\([^)]*\)\s*$/, '')
  return out.replace(/[-&\s]+/g, ' ').trim()
}

// our normalised make -> the normalised RDW `merk` it should be looked up under.
// Only for the cases normalisation itself cannot reach.
export const MAKE_ALIASES = {
  VW: 'VOLKSWAGEN',            // our 3rd-largest brand; silently zero without this
  MERCEDES: 'MERCEDES BENZ',   // our largest brand, and RDW hyphenates
  'DS AUTOMOBILES': 'DS',
  CORVETTE: 'CHEVROLET',       // many-to-one, but `Chevrolet` ∩ `Corvette` = 0 models,
                               // so it is safe at brand grain (§3d fix 4)
}

// The token `handelsbenaming` may lead with when `merk` has collapsed the sub-brand.
const SUBBRAND_PREFIX = { ABARTH: 'ABARTH', CUPRA: 'CUPRA', DS: 'DS AUTOMOBILES' }

// RDW `merk` -> the merk we hold the vocabulary under. These are REGISTRATION entities, not
// marques: RDW files the i3 under a separate type-approval holder `BMW I` and the CNG Mondeo
// under `FORD-CNG-TECHNIK`, both of which are the parent brand's cars in our layer. Kept apart
// from `MAKE_ALIASES` (which points the other way, our make -> RDW merk) because these two
// collapse an RDW-side entity, and the failure they fix is `unknown merk`, not a model miss.
export const MERK_ALIASES = {
  'BMW I': 'BMW',
  'FORD CNG TECHNIK': 'FORD',
}

// GEN-SPLIT-1. AutoUncle's taxonomy splits some nameplates by GENERATION — `Golf I` ..
// `Golf VIII`, `Megane II` .. `Megane IV`, `Grand Scenic II` .. `IV` — while RDW's
// `handelsbenaming` only ever says `GOLF`. Where a bare row is also published it is a RUMP
// holding whatever the taxonomy failed to assign a generation to, and the plate lands on it:
// NL's bare `Golf` row holds 64 listings, ALL of them e-Golfs at p50 EUR 12,750, against 9,083
// listings sitting in the eight generation rows. Every Dutch Golf plate was valued as an EV.
//
// The numerals are matched UPPERCASE and `X` is excluded, and both restrictions are there
// because of a false positive in the live layer, not for tidiness:
//   * lowercase `iV` is Skoda's electric sub-brand (`Enyaq iV`), not a fourth Enyaq;
//   * a trailing `X` is a modern MODEL letter, not a generation — `Aygo X`, `Mokka X`,
//     `Crossland X`, `Grandland X`, `C4 X`, `C5 X` and `Tesla Model X` are all different cars
//     from their base, and `Model` is not a nameplate at all.
// `IX` is kept: it only ever appears as a ninth generation (`Jaguar MK IX`).
//
// Which string this is applied to is therefore load-bearing, and the test found it before the
// live data did: `normaliseModel` UPPERCASES, so run against the matcher's own vocabulary the
// case rule is inert and `Enyaq iV` reads as a fourth Enyaq. It runs on the RAW published
// `model` instead, which is the only place the distinction still exists.
const GENERATION_NUMERAL = /^(?:I{1,3}|IV|VI{0,3}|IX)$/

// REIHE-1. The German series designation RDW files a large slice of the BMW parc under.
// Anchored and whole-string: it must NOT fire on a real nameplate that merely contains the
// word, and it is deliberately blind to `X REIHE` (see `_resolveSeries`).
const SERIES_DESIGNATION = /^([0-9])ER REIHE$/

// RDW publishes `nettomaximumvermogen` in kW; our layer publishes `median_hp` in PK. Must
// stay identical to `matcher.py`'s constant of the same name.
const KW_TO_HP = 1.35962

// How far the plate's own power may sit from a candidate badge's median before the match is
// refused. RELATIVE, because badge steps scale with the car — see `_resolveSeries`.
const SERIES_HP_TOLERANCE_FRAC = 0.25

// How decisively the plate must favour its winner, on the scale of the GAP between the two
// badges: `(runnerD - bestD) / gap` is how far from equidistant the plate sits, 0 at the
// midpoint and 1 on a badge. And even an indecisive plate only matters when the two badges
// disagree about the MONEY — `520`/`523` are 3.3x apart in p50 and genuinely have no answer,
// `523`/`525` are 1.34x apart, well inside a published band's own width.
// Must stay identical to matcher.py's constants of the same names.
// 0.25 is FITTED (cycle 078, scripts/diag/fit_series_decisive.py over 395 `NER REIHE`
// plates, 357 hand-labelled badges). It strictly dominates the 0.5 it replaces: 382 served
// instead of 350 AND 78.7% of served correct instead of 76.9%. Keep in step with
// src/kenteken/matcher.py — the parity fixtures compare the two.
const SERIES_DECISIVE_FRAC = 0.25
const SERIES_TIE_PRICE_RATIO = 1.5

/**
 * `Golf VII` -> `Golf`; `Aygo X` / `Enyaq iV` / a lone numeral -> null.
 *
 * Takes the model AS PUBLISHED, not normalised — see the note above.
 */
export function generationBase(model) {
  const parts = String(model || '').split(' ')
  if (parts.length < 2 || !GENERATION_NUMERAL.test(parts[parts.length - 1])) return null
  return parts.slice(0, -1).join(' ')
}

// --- §3b/§3c model ----------------------------------------------------------

/**
 * `CITROEN C1` + make `CITROEN` -> `C1`; `206+` -> `206 PLUS`.
 *
 * Step order is load-bearing. The `+` is promoted to a word BEFORE punctuation collapsing,
 * because collapsing first destroys it and silently merges `206`/`206+`, `RX450h`/`RX450h+`
 * and five other pairs — 75 published rows into 7 keys, resolving toward the dearer member.
 */
export function normaliseModel(raw, makeNorm = '') {
  if (!raw) return ''
  let out = stripDiacritics(String(raw)).toUpperCase()
  if (makeNorm) {
    // `handelsbenaming` is sometimes brand-prefixed and sometimes bare.
    if (out.startsWith(makeNorm + ' ')) out = out.slice(makeNorm.length + 1)
    else if (out === makeNorm) out = ''
  }
  out = out.replace(/\+(?=\s|$)/g, ' PLUS')
  out = out.replace(/[^A-Z0-9]+/g, ' ')
  return out.split(/\s+/).filter(Boolean).join(' ')
}

/**
 * Which of OUR make strings could an RDW `merk` mean?
 *
 * Answered against the live vocabulary rather than a hardcoded list, because the published
 * make set changes as brands are added. Returns the makes EXACTLY as stored, since that is
 * what a PocketBase `make = '...'` filter needs.
 *
 * `car_brands` is NOT usable as this vocabulary — measured 2026-08-24, it is missing both
 * `Mercedes` and `VW`, our two largest brands, and carries `SEAT` and `Seat` as separate
 * rows. Deriving the makes from `market_variants` itself is the only source that cannot
 * disagree with the rows we are about to join against.
 */
export function candidateMakes(merkNorm, allMakes) {
  // The RDW-side alias is applied HERE and not only in `match()`, because this function is
  // what decides which makes' rows are fetched at all: `valuateKenteken` short-circuits on an
  // empty list, so a `BMW I` plate would never reach the matcher that knows the alias.
  const target = MERK_ALIASES[merkNorm] || merkNorm
  const out = []
  for (const make of allMakes) {
    const ourNorm = normaliseMake(make)
    if (!ourNorm) continue
    if ((MAKE_ALIASES[ourNorm] || ourNorm) === target) out.push(make)
  }
  return out
}

/**
 * Outcome of one plate lookup. The degraded states are not failures — they are the honest
 * answer, and `ambiguous_*` must never be silently resolved.
 *
 * state: model_fuel_hp | model_fuel | model | ambiguous_model | ambiguous_make | no_match
 */
function result(state, extra = {}) {
  return {
    state,
    make: null,
    model: null,
    fuel: null,
    row: null,
    grain: null,
    candidates: [],
    note: '',
    matched: state === 'model_fuel_hp' || state === 'model_fuel' || state === 'model',
    ...extra,
  }
}

// HP-GRAIN-1 (2026-09-05): the variant grain is unreachable from a plate -- RDW ships
// type-approval codes and we hold human trim names -- but POWER is reachable: every plate
// carries `nettomaximumvermogen` in kW. The publisher now emits a `model_fuel_hp` cell per
// power band, so a plate is valued against its own engine rather than the nameplate's.
//
// Measured on a real car. G937KH (2011 Giulietta 1.4 TB, 88 kW = 120 pk, 195,000 km) matched
// the model+fuel cell whose MEDIAN POWER IS 170 pk: EUR 6,107 with a EUR 3,006-12,721 band at
// 5% confidence. Its own 100-129 cell is n=52, median power 120 pk, median year 2011,
// p50 EUR 6,270. The owner bought that car for EUR 4,200 and sold it below the tool's number.
//
// Edges must stay identical to scripts/build_market_variants.py; a band the publisher never
// writes selects nothing at all.
const HP_BAND_EDGES = [0, 100, 130, 160, 200, 250, 320, 1e9]
const HP_BAND_LABELS = ['<100', '100-129', '130-159', '160-199', '200-249', '250-319', '320+']

/** Band label for a horsepower figure, or null when power is unknown or nonsense. */
export function hpBandForHp(hp) {
  const v = Number(hp)
  if (!Number.isFinite(v) || v <= 0) return null
  for (let i = 0; i < HP_BAND_EDGES.length - 1; i += 1) {
    if (v >= HP_BAND_EDGES[i] && v < HP_BAND_EDGES[i + 1]) return HP_BAND_LABELS[i]
  }
  return null
}

/** Band label for the kW figure RDW publishes, or null when it is absent. */
export function hpBandForKw(powerKw) {
  const v = Number(powerKw)
  if (!Number.isFinite(v) || v <= 0) return null
  return hpBandForHp(v * KW_TO_HP)
}

/** Index of the published layer, keyed the way a plate can reach it. */
export class KentekenMatcher {
  constructor(rows) {
    this.byMakeModelFuel = new Map()
    this.byMakeModel = new Map()
    // HP-GRAIN-1: `make\0model\0fuel\0band` -> the published cell for that engine.
    this.byMakeModelFuelHp = new Map()
    // our normalised make -> Set of normalised models, the set step 5 scans
    this.modelsByMake = new Map()
    // our normalised make -> Map(space-collapsed model -> normalised model). RDW writes a
    // Mercedes A180 as `A 180` and a Hyundai i30 as `I 30`; we write them glued. The tight
    // key is the only difference, so it is worth a fallback — but ONLY where it is injective
    // within the make. Collisions are dropped, not resolved, because an arbitrary pick
    // returns another car's band with a confidence attached (§3c).
    this.tight = new Map()
    this.tightCollisions = new Map()
    // Published rows whose NORMALISED key collides — recorded, never silently dropped.
    this.keyCollisions = new Map()
    // our normalised make -> the make string as published, for display.
    this.rawMake = new Map()
    // GEN-SPLIT-1: `${make} ${bare nameplate}` -> Set of generation model names.
    this.generations = new Map()

    for (const row of rows || []) {
      const grain = row.grain
      // HP-GRAIN-1: index the power cells and take NO other bookkeeping from them. They
      // describe models the model_fuel rows already registered, so the model vocabulary,
      // the generation sets and the tight index must not see the same model twice.
      if (grain === 'model_fuel_hp') {
        const hpMake = normaliseMake(row.make)
        const hpModel = normaliseModel(row.model)
        if (hpMake && hpModel && row.hp_band) {
          this._insert(this.byMakeModelFuelHp,
            `${hpMake} ${hpModel} ${row.fuel} ${row.hp_band}`, row)
        }
        continue
      }
      // variant_fuel is unreachable from a plate — settled, cycle 013
      if (grain !== 'model' && grain !== 'model_fuel') continue
      const ourMake = normaliseMake(row.make)
      const model = normaliseModel(row.model)
      if (!ourMake || !model) continue
      this.rawMake.set(ourMake, row.make)
      if (grain === 'model_fuel') {
        this._insert(this.byMakeModelFuel, `${ourMake} ${model} ${row.fuel}`, row)
      } else {
        this._insert(this.byMakeModel, `${ourMake} ${model}`, row)
      }
      if (!this.modelsByMake.has(ourMake)) this.modelsByMake.set(ourMake, new Set())
      this.modelsByMake.get(ourMake).add(model)
      const rawBase = generationBase(row.model)
      const base = rawBase ? normaliseModel(rawBase) : ''
      if (base) {
        const key = `${ourMake} ${base}`
        if (!this.generations.has(key)) this.generations.set(key, new Set())
        this.generations.get(key).add(model)
      }
    }

    this._buildTightIndex()
  }

  /**
   * Put a row in an index, resolving a key collision by EVIDENCE, not by insertion order.
   *
   * The published layer really does collide: `Porsche Cayenne Coupe` and `Cayenne Coupé` are
   * one nameplate spelled two ways, and the accent is all that separates a hybrid band of
   * n=5 at EUR 79,980 from one of n=34 at EUR 110,900 — **1.39x apart**. A plain assignment
   * keeps whichever row arrived last, which is a property of ordering, not of the data.
   *
   * Refusing (the §3c ambiguity answer) is wrong HERE and right for `RX450h`/`RX450h+`: the
   * `+` pair is two different powertrains, so no evidence can choose between them, while
   * these two are the same car and refusing denies every Cayenne Coupé plate a valuation it
   * has 34 listings for. So the deeper row wins — `n_listings` is a total, order-independent
   * ordering — with confidence and then the raw string as tie-breakers.
   */
  _insert(index, key, row) {
    const prior = index.get(key)
    if (prior === undefined) {
      index.set(key, row)
      return
    }
    if (!this.keyCollisions.has(key)) this.keyCollisions.set(key, [String(prior.model)])
    this.keyCollisions.get(key).push(String(row.model))
    const rank = r => [Number(r.n_listings || 0), Number(r.confidence || 0), String(r.model || '')]
    const [an, ac, as] = rank(row)
    const [bn, bc, bs] = rank(prior)
    if (an > bn || (an === bn && (ac > bc || (ac === bc && as > bs)))) index.set(key, row)
  }

  /** Space-collapsed fallback keys, with §3c's injectivity assertion applied. */
  _buildTightIndex() {
    for (const [ourMake, models] of this.modelsByMake) {
      const seen = new Map()
      const collided = new Set()
      for (const model of models) {
        const tight = model.replace(/ /g, '')
        if (seen.has(tight) && seen.get(tight) !== model) collided.add(tight)
        seen.set(tight, model)
      }
      for (const tight of collided) seen.delete(tight)
      this.tight.set(ourMake, seen)
      if (collided.size) this.tightCollisions.set(ourMake, collided)
    }
  }

  /**
   * `ALFA GIULIETTA` under merk `ALFA ROMEO` -> `GIULIETTA`.
   *
   * `normaliseModel` strips the make only when `handelsbenaming` leads with the WHOLE merk,
   * and RDW very often writes just part of it: the operator's own plate 9-SZS-20 is
   * `merk=ALFA ROMEO, handelsbenaming=ALFA GIULIETTA` while our vocabulary holds
   * `Giulietta`, so the plate that motivated this whole feature was a `no_match`.
   *
   * Applied only as a RETRY, after the unmodified query has already failed, which makes it
   * strictly additive — it can turn a miss into a hit and can never change a plate that
   * already resolved. That matters because the token being dropped is sometimes a real
   * model word: Fiat sells a `500` and Smart a `Smart`, and a make-token prefix is only
   * junk when keeping it matched nothing. The last token is never dropped.
   */
  static _dropLeadingMakeTokens(query, merkNorm) {
    const tokens = new Set(merkNorm.split(' '))
    const parts = query.split(' ')
    let i = 0
    while (i < parts.length - 1 && tokens.has(parts[i])) i++
    return i ? parts.slice(i).join(' ') : query
  }

  /**
   * `MAZDA2` -> `2`. RDW glues the make onto a numeric nameplate; we publish it bare.
   *
   * Only when a DIGIT follows — a letter there belongs to a longer word (`MAZDASPEED`).
   * Even so this MUST be retry-only, and `DS3 CROSSBACK` under merk `DS` is the proof:
   * applied eagerly it strips a `DS` that is part of the nameplate and turns a matched plate
   * into `3 CROSSBACK`, a miss. It cost one plate in cycle 048's own measurement.
   */
  static _dropGluedMakePrefix(query, merkNorm) {
    if (merkNorm && query.startsWith(merkNorm) && /^[0-9]/.test(query.slice(merkNorm.length))) {
      return query.slice(merkNorm.length)
    }
    return query
  }

  /**
   * `155332 GOLF GTI CABRIOLET` -> `GOLF GTI CABRIOLET`.
   *
   * VW files a chassis type code in front of the nameplate. The threshold is FIVE digits and
   * it is not a guess: no published model anywhere in the layer has a leading pure numeric
   * token wider than four (`1502`, `2002`, `2800`), so a >=5-digit token cannot be one of
   * ours and dropping it cannot shadow a real nameplate. A `126 PERSONAL` or a `Fiat 500` is
   * untouched by construction, not by luck.
   */
  static _dropTypeCodePrefix(query) {
    const parts = query.split(' ')
    if (parts.length > 1 && /^[0-9]{5,}$/.test(parts[0])) return parts.slice(1).join(' ')
    return query
  }

  /**
   * `320D` -> `320`, `218I ACTIVE TOURER` -> `218 ACTIVE TOURER`, `M340I` -> `M340`.
   *
   * RDW writes BMW's fuel letter into the type name; we publish the bare code. Only `I` and
   * `D` are dropped, and the exclusion of `E` is the point of the rule rather than an
   * omission: `320e`/`330e`/`M760e`/`GLC300e` are plug-in hybrids with their OWN published
   * rows, typically far dearer than the petrol car of the same code, so treating that letter
   * as noise would resolve a PHEV onto the cheap row. `I`/`D` say petrol/diesel, which the
   * fuel key already carries; `E` says powertrain, which nothing else does.
   *
   * Rewriting the QUERY rather than loosening the prefix rule is what keeps the body style:
   * `218I ACTIVE TOURER` becomes an exact hit on `218 ACTIVE TOURER`, where a digit->letter
   * boundary in the matcher would only have reached the bare `218`.
   */
  static _dropEngineLetter(query) {
    const parts = query.split(' ')
    const head = /^([A-Z]?[0-9]{3})(I|D)$/.exec(parts[0])
    if (!head) return query
    parts[0] = head[1]
    return parts.join(' ')
  }

  /**
   * `E 350 CGI` -> `E350`: glue the first k tokens, longest k first, tight-index it.
   *
   * Mercedes is the whole reason this exists. RDW writes an engine code spaced (`E 350`,
   * `GLC 300 e`) and we publish it glued (`E350`, `GLC300E`), which step 5b already handles —
   * but only for a query that is JUST the code. RDW routinely appends a fuel or drivetrain
   * qualifier we do not publish (`CGI`, `CDI`, `4MATIC`, `COUPE`), and the whole-string tight
   * lookup then misses.
   *
   * Longest k first is load-bearing and is what keeps the powertrain: `GLC 300 e 4MATIC`
   * finds `GLC300E` (the plug-in, its own published row) at k=3 and would find the petrol
   * `GLC300` at k=2. Falling to the shorter glue only happens when the longer one is not
   * published at all.
   */
  _resolveGluedPrefix(ourMake, query) {
    const tight = this.tight.get(ourMake)
    if (!tight) return null
    const parts = query.split(' ')
    for (let k = parts.length; k > 0; k--) {
      const hit = tight.get(parts.slice(0, k).join(''))
      if (hit) return hit
    }
    return null
  }

  /** The row `match` would actually serve for this key: the fuel grain, else the roll-up. */
  _serveRow(ourMake, model, fuel) {
    return this.byMakeModelFuel.get(`${ourMake} ${model} ${fuel}`)
        ?? this.byMakeModel.get(`${ourMake} ${model}`)
        ?? null
  }

  /**
   * How many listings the row's curve was fitted on AT `year`.
   *
   * Zero when the row does not reach the year at all — including when it has no curve,
   * which is the honest reading: a row with no fitted years has no evidence about any
   * particular one. Falls back to the whole-cell total only for a row published before
   * `age_curve_points` carried its per-year count (CELL-EVIDENCE-1).
   */
  static _evidenceAt(row, year) {
    if (!row) return 0
    const points = row.age_curve_points
    if (!Array.isArray(points) || points.length === 0) return 0
    for (const p of points) {
      if (p && Number(p[0]) === year) {
        return p.length > 2 && p[2] ? Number(p[2]) : Number(row.n_listings || 0)
      }
    }
    return 0
  }

  /**
   * GEN-SPLIT-1: `GOLF` + a 2004 plate -> `GOLF IV`. Null when nothing covers the year.
   *
   * Only ever fires where the taxonomy actually splits the nameplate (`generationBase`), and
   * only on generations whose curve was FITTED across the plate's year — not merely
   * published. A generation whose evidence stops in 2006 cannot answer for a 2019 car, and
   * saying so leaves the caller exactly where it was.
   *
   * Ranked by the listings the row holds AT THE PLATE'S OWN YEAR, not by its total.
   * Generations overlap at the changeover (`Golf IV` fits 2000-2006 and `Golf V` 2004-2008,
   * because NL stock does), and a total favours whichever generation sold best over its whole
   * life rather than the one this car belongs to — `Golf VII` (n=4,387) would answer for every
   * year it happens to span.
   *
   * The incumbent bare row is ranked on the same scale and has to be BEATEN, which is what
   * stops this from being a blanket "prefer the generation". A bare row is only sometimes a
   * rump. `Clio` holds 5,357 listings against 268 across `Clio II`-`V`, and redirecting a 2010
   * plate onto `Clio II` (n=42, fitted 2001-2011 and so nominally covering the year) traded
   * 5,357 cars of evidence for 42 and took the plate from EUR 2,625 to EUR 1,684 — a real 2010
   * Clio is neither. Where the bare row genuinely is a rump the comparison is not close:
   * `Golf` holds 64 e-Golfs fitted 2015-2021, so at 2004 it scores zero.
   */
  _resolveGeneration(ourMake, model, fuel, year) {
    if (year === null || year === undefined || !Number.isFinite(Number(year))) return null
    const gens = this.generations.get(`${ourMake} ${model}`)
    if (!gens) return null
    const y = Number(year)
    let bestName = null
    let bestRank = KentekenMatcher._evidenceAt(this._serveRow(ourMake, model, fuel), y)
    for (const gen of [...gens].sort()) {
      const row = this._serveRow(ourMake, gen, fuel)
      if (!row) continue
      const lo = row.age_curve_min_year
      const hi = row.age_curve_max_year
      if (lo === null || lo === undefined || hi === null || hi === undefined) continue
      if (y < Number(lo) || y > Number(hi)) continue
      const rank = KentekenMatcher._evidenceAt(row, y)
      if (rank > bestRank) {
        bestRank = rank
        bestName = gen
      }
    }
    return bestName
  }

  /** Exact, else longest prefix on a token boundary. Ties are ambiguous. */
  _resolveModel(ourMake, query) {
    const vocab = this.modelsByMake.get(ourMake)
    if (!vocab) return [null, []]
    if (vocab.has(query)) return [query, [query]]
    // Anchored at the START of the query, on a token boundary: `116` matches `116 I`
    // but never `1160`. A bare substring match would return another model's band.
    const cands = [...vocab].filter(m => query.startsWith(m + ' '))
    if (cands.length === 0) {
      // Step 5b: the same string with the spaces taken out. `A 180` -> `A180`.
      const tight = this.tight.get(ourMake)?.get(query.replace(/ /g, ''))
      return tight ? [tight, [tight]] : [null, []]
    }
    const longest = Math.max(...cands.map(m => m.length))
    const tied = cands.filter(m => m.length === longest).sort()
    if (tied.length > 1) return [null, tied]
    return [tied[0], tied]
  }

  /**
   * REIHE-1: `3ER REIHE` + 125 kW + a 2002 plate -> `320`. The badge IS the power class.
   *
   * Port of `_resolve_series` in matcher.py — read the rationale there; it is the long one.
   * In short: RDW files a large slice of the German parc under the GERMAN SERIES NAME with no
   * engine code, 8 of the 23 surviving `no_match` plates in the seed-42 sample of 500. There
   * is no series-level row to fall back to (the only `1-Series` row holds n=1, no curve), and
   * a BMW badge is an ordered power ladder, so the plate's own `nettomaximumvermogen` recovers
   * it: 170 hp/2002 -> `320`, 306 hp/2007 -> `335`, 143 hp/2008 -> `118`.
   *
   * Guards, all of which matter (each is measured in the Python docstring): BARE codes only —
   * a series name carries no body signal and `320 CABRIOLET` is the nearest neighbour on power
   * for the 2002 plate, which would move it EUR 14,950 -> EUR 10,750; fitted at the plate's
   * year, not merely published; a RELATIVE power tolerance, because badge steps scale with the
   * car; and a refusal on a tie, because `520`/`523` sit 6 hp and EUR 13,753 apart.
   *
   * `X REIHE` deliberately does not resolve: the X family is a size ladder, not a power one.
   *
   * @returns {[string|null, string]} the model, and why it did or did not resolve
   */
  _resolveSeries(ourMake, query, fuel, year, powerKw) {
    if (year === null || year === undefined || !powerKw) return [null, '']
    const designation = SERIES_DESIGNATION.exec(query)
    if (!designation) return [null, '']
    const plateHp = Number(powerKw) * KW_TO_HP
    const bare = new RegExp(`^${designation[1]}[0-9]{2}$`)

    const scored = []
    for (const model of [...(this.modelsByMake.get(ourMake) || [])].sort()) {
      if (!bare.test(model)) continue
      const row = this._serveRow(ourMake, model, fuel)
      if (!row) continue
      const lo = row.age_curve_min_year
      const hi = row.age_curve_max_year
      if (lo === null || lo === undefined || hi === null || hi === undefined) continue
      if (!(Number(lo) <= year && year <= Number(hi))) continue
      const hp = Number(row.median_hp)
      if (!hp) continue
      scored.push([Math.abs(hp - plateHp), model, hp, Number(row.p50) || 0])
    }
    if (scored.length === 0) return [null, '']
    scored.sort((a, b) => a[0] - b[0] || (a[1] < b[1] ? -1 : 1))
    const [bestD, bestModel, bestHp, bestP50] = scored[0]
    if (bestD > SERIES_HP_TOLERANCE_FRAC * plateHp) return [null, 'series_out_of_range']
    if (scored.length > 1) {
      const [runnerD, , runnerHp, runnerP50] = scored[1]
      // Two badges published at the same median power cannot be told apart at all, so the
      // ratio is undefined and the answer is "not decisive" rather than a divide.
      const gap = Math.abs(bestHp - runnerHp)
      const decisive = gap > 0 && runnerD - bestD >= SERIES_DECISIVE_FRAC * gap
      const prices = [bestP50, runnerP50].filter(p => p > 0)
      const disagree =
        prices.length === 2 &&
        Math.max(...prices) / Math.min(...prices) > SERIES_TIE_PRICE_RATIO
      if (!decisive && disagree) return [null, 'series_power_tie']
    }
    return [bestModel, 'series_power']
  }

  match(merk, handelsbenaming, fuel, year = null, powerKw = null) {
    const rawMerkNorm = normaliseMake(merk)
    const merkNorm = MERK_ALIASES[rawMerkNorm] || rawMerkNorm
    const query = normaliseModel(handelsbenaming, merkNorm)
    if (!query) return result('no_match', { note: 'empty model string' })

    // Which of our makes are actually present in the rows we were given?
    const makes = [...this.modelsByMake.keys()].filter(
      m => (MAKE_ALIASES[m] || m) === merkNorm
    )
    if (makes.length === 0) return result('no_match', { note: `unknown merk ${merkNorm}` })

    // A colliding group (§3d). Try the `handelsbenaming` prefix as tie-breaker first — if RDW
    // writes `CUPRA LEON`, the sub-brand is recoverable even though `merk` said SEAT.
    let candidates = makes
    let via = ''
    if (makes.length > 1) {
      const sub = SUBBRAND_PREFIX[query.split(' ')[0]]
      if (sub && makes.includes(sub)) {
        candidates = [sub]
        via = 'subbrand_prefix'
      }
    }

    // Resolve the model inside every candidate make, then see whether the makes disagree.
    // The retry with the make tokens dropped runs only when the plain query found nothing
    // anywhere, so it cannot re-decide a plate that already resolved.
    const hits = []
    let tieNote = ''
    let seriesNote = ''
    const attempts = [query]
    for (const variant of [
      KentekenMatcher._dropLeadingMakeTokens(query, merkNorm),
      KentekenMatcher._dropGluedMakePrefix(query, merkNorm),
      KentekenMatcher._dropTypeCodePrefix(query),
      KentekenMatcher._dropEngineLetter(query),
    ]) {
      if (variant && !attempts.includes(variant)) attempts.push(variant)
    }
    for (const attempt of attempts) {
      for (const ourMake of candidates) {
        const [model, tied] = this._resolveModel(ourMake, attempt)
        if (model) hits.push([ourMake, model])
        else if (tied.length > 1) tieNote = `model tie: ${tied.join(',')}`
      }
      if (hits.length) break
    }

    if (hits.length === 0) {
      // Last resort, after every plain form has failed everywhere: the spaced-engine-code
      // glue. Ordered last so it can only ever convert a miss.
      for (const attempt of attempts) {
        for (const ourMake of candidates) {
          const model = this._resolveGluedPrefix(ourMake, attempt)
          if (model) hits.push([ourMake, model])
        }
        if (hits.length) break
      }
    }

    if (hits.length === 0) {
      // GEN-SPLIT-1, miss half: the nameplate is published ONLY as generations
      // (`Grand Scenic II..IV` with no bare row). Ordered here so it, too, can only ever
      // convert a miss.
      for (const attempt of attempts) {
        for (const ourMake of candidates) {
          const gen = this._resolveGeneration(ourMake, attempt, fuel, year)
          if (gen) hits.push([ourMake, gen])
        }
        if (hits.length) break
      }
    }

    if (hits.length === 0) {
      // REIHE-1: the handelsbenaming names a SERIES, not a model. Ordered last, after every
      // string-shaped rule has failed, because it is the only rule here that resolves on
      // evidence outside the name (the plate's own power).
      for (const ourMake of candidates) {
        const [model, why] = this._resolveSeries(ourMake, query, fuel, year, powerKw)
        if (model) {
          hits.push([ourMake, model])
          via = via ? `${via}; ${why}` : why
        } else if (why) {
          // A refused series is a `no_match`, NOT an `ambiguous_model`: the funnel counts
          // those separately and a power tie is not a vocabulary tie.
          seriesNote = why
        }
      }
    }

    if (hits.length === 0) {
      return result(tieNote ? 'ambiguous_model' : 'no_match', {
        note: tieNote || seriesNote || `no model for ${query} under ${merkNorm}`,
      })
    }
    if (hits.length > 1) {
      // The same nameplate exists under two of our makes for one RDW merk — a `Fiat 500` /
      // `Abarth 500` shape. 89.8% of that mass is the cheap parent car, so a coin flip is
      // optimistically biased. Refuse.
      return result('ambiguous_make', {
        model: query,
        candidates: hits.map(([mk, md]) => `${this.rawMake.get(mk) || mk}:${md}`).sort(),
        note: `merk ${merkNorm} spans ${hits.length} of our makes`,
      })
    }

    const [ourMake, resolved] = hits[0]
    // GEN-SPLIT-1, hit half: `GOLF` resolved, but the bare row is the taxonomy's rump.
    // Applied AFTER the make/ambiguity rules so it can never change which make answers, and
    // only where a generation was fitted across this plate's year with more evidence at it.
    const generation = this._resolveGeneration(ourMake, resolved, fuel, year)
    const model = generation || resolved
    if (generation) via = via ? `${via}; generation` : 'generation'
    const display = this.rawMake.get(ourMake) || ourMake
    // HP-GRAIN-1: the plate's own engine first. This is only ever a REFINEMENT of the
    // model_fuel answer -- same make, same model, same fuel, narrower population -- so it
    // cannot change which car answered, only which engine of it.
    const plateHpBand = hpBandForKw(powerKw)
    const rowHp = plateHpBand
      ? this.byMakeModelFuelHp.get(`${ourMake} ${model} ${fuel} ${plateHpBand}`)
      : undefined
    if (rowHp !== undefined) {
      return result('model_fuel_hp', {
        make: display, model, fuel, row: rowHp, grain: 'model_fuel_hp',
        note: via ? `${via}; power ${plateHpBand}` : `power ${plateHpBand}`,
      })
    }
    const rowFuel = this.byMakeModelFuel.get(`${ourMake} ${model} ${fuel}`)
    if (rowFuel !== undefined) {
      return result('model_fuel', {
        make: display, model, fuel, row: rowFuel, grain: 'model_fuel', note: via,
      })
    }
    const rowModel = this.byMakeModel.get(`${ourMake} ${model}`)
    if (rowModel !== undefined) {
      return result('model', {
        make: display, model, fuel, row: rowModel, grain: 'model', note: via || 'fuel fallback',
      })
    }
    return result('no_match', {
      make: display, model, fuel, note: 'model known, no publishable row',
    })
  }
}
